/**
 * PlatformHealthDot — tiny admin-only visibility widget.
 *
 * Operator brief (May 24, 2026):
 *   "Admin dashboard only · uses existing GET /api/uploads/diagnostic ·
 *    shows a small green/amber/red status indicator · tooltip or
 *    expandable detail can show free disk, emergency floor, ffmpeg
 *    slots available, ffmpeg slots busy, timestamp · no cleanup,
 *    retry, restart, or mutation actions. This is visibility only,
 *    not an operations control centre."
 *
 * The widget renders a single 8 px pulsing dot in the admin header.
 * Clicking opens a popover with the raw counters. There are NO
 * action buttons inside the popover — by design.
 *
 * Polls `/api/uploads/diagnostic` every 30 s while mounted. Stops
 * polling when the popover is closed and the dot is unfocused.
 */
import React from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000;

const STATUS_PALETTE = {
  healthy: { dot: "bg-emerald-500", ring: "ring-emerald-300", label: "Healthy" },
  warning: { dot: "bg-amber-500", ring: "ring-amber-300", label: "Warning" },
  critical: { dot: "bg-rose-500", ring: "ring-rose-300", label: "Critical" },
  unknown: { dot: "bg-slate-400", ring: "ring-slate-300", label: "Unknown" },
};

function _formatBytes(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

function _formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function PlatformHealthDot() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const mountedRef = React.useRef(true);

  const fetchOnce = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/uploads/diagnostic`, {
        withCredentials: true,
        timeout: 8000,
      });
      if (!mountedRef.current) return;
      setData(r.data);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.response?.status || err?.message || "fetch_failed");
      setData(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchOnce]);

  const status = data?.status || (error ? "unknown" : (loading ? "unknown" : "unknown"));
  const palette = STATUS_PALETTE[status] || STATUS_PALETTE.unknown;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="platform-health-dot"
          aria-label={`Platform health: ${palette.label}`}
          className={[
            "relative inline-flex items-center justify-center",
            "w-6 h-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800",
            "transition-colors focus:outline-none focus:ring-2",
            palette.ring,
          ].join(" ")}
        >
          <span
            data-testid="platform-health-dot-indicator"
            data-status={status}
            className={[
              "block w-2.5 h-2.5 rounded-full",
              palette.dot,
              status === "critical" ? "animate-pulse" : "",
            ].join(" ")}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-72 p-3"
        data-testid="platform-health-popover"
      >
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`block w-2 h-2 rounded-full ${palette.dot}`}
            aria-hidden="true"
          />
          <span
            className="text-xs font-semibold text-slate-700 dark:text-slate-200"
            data-testid="platform-health-status-label"
          >
            Platform health · {palette.label}
          </span>
        </div>

        {error && !data && (
          <p
            className="text-[11px] text-rose-600 dark:text-rose-400"
            data-testid="platform-health-error"
          >
            Could not read diagnostic ({String(error)}).
          </p>
        )}

        {data && (
          <dl
            className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]"
            data-testid="platform-health-details"
          >
            <dt className="text-slate-500">Free disk</dt>
            <dd
              data-testid="platform-health-free-disk"
              className="text-right font-mono text-slate-700 dark:text-slate-200"
            >
              {_formatBytes(data.free_disk_bytes)}
            </dd>

            <dt className="text-slate-500">Emergency floor</dt>
            <dd
              data-testid="platform-health-emergency-floor"
              className="text-right font-mono text-slate-700 dark:text-slate-200"
            >
              {_formatBytes(data.emergency_floor_bytes)}
            </dd>

            <dt className="text-slate-500">ffmpeg slots free</dt>
            <dd
              data-testid="platform-health-ffmpeg-slots-available"
              className="text-right font-mono text-slate-700 dark:text-slate-200"
            >
              {data.ffmpeg_slots_available}/{data.ffmpeg_slots_capacity}
            </dd>

            <dt className="text-slate-500">ffmpeg slots busy</dt>
            <dd
              data-testid="platform-health-ffmpeg-slots-busy"
              className="text-right font-mono text-slate-700 dark:text-slate-200"
            >
              {data.ffmpeg_slots_busy}
            </dd>

            <dt className="text-slate-500 col-span-2 mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide">
              Legacy kill switches
            </dt>

            <dt className="text-slate-500">Lesson-review upload</dt>
            <dd
              data-testid="platform-health-legacy-lesson-review-upload"
              data-open={data.legacy_lesson_review_upload_enabled ? "true" : "false"}
              className={
                "text-right font-mono " +
                (data.legacy_lesson_review_upload_enabled
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-emerald-600 dark:text-emerald-400")
              }
            >
              {data.legacy_lesson_review_upload_enabled ? "open" : "closed"}
            </dd>

            <dt className="text-slate-500">Chunked clip-basket</dt>
            <dd
              data-testid="platform-health-legacy-chunked-clip-basket"
              data-open={data.legacy_chunked_clip_basket_enabled ? "true" : "false"}
              className={
                "text-right font-mono " +
                (data.legacy_chunked_clip_basket_enabled
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-emerald-600 dark:text-emerald-400")
              }
            >
              {data.legacy_chunked_clip_basket_enabled ? "open" : "closed"}
            </dd>

            <dt className="text-slate-500 col-span-2 mt-1 pt-1 border-t border-slate-100 dark:border-slate-800 text-[10px]">
              Last refresh ·{" "}
              <span data-testid="platform-health-timestamp">
                {_formatTimestamp(data.timestamp)}
              </span>
            </dt>
          </dl>
        )}

        <p
          className="mt-2 text-[10px] text-slate-400"
          data-testid="platform-health-readonly-note"
        >
          Read-only. No actions available here.
        </p>
      </PopoverContent>
    </Popover>
  );
}
